import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector, selectActiveTenantId } from '@/store/hooks';
import { resetForm as resetOutboundForm } from '@/store/slices/createOutboundSlice';

interface RouteGuard {
  match: (path: string) => boolean;
  overview: string;
  resetActions?: (() => { type: string })[];
}

const GUARDED_ROUTES: RouteGuard[] = [
  {
    match: (p) => p === '/outbound/new' || p.startsWith('/outbound/draft/'),
    overview: '/outbound',
    resetActions: [resetOutboundForm],
  },
  {
    match: (p) => p.startsWith('/outbound/') && !p.startsWith('/outbound/new') && !p.startsWith('/outbound/draft/'),
    overview: '/outbound',
  },
  {
    match: (p) => p.startsWith('/inbound/') && p !== '/inbound/new',
    overview: '/inbound',
  },
  {
    match: (p) => p.startsWith('/road-transport/') && p !== '/road-transport',
    overview: '/road-transport',
  },
];

// One-shot bypass flag: when set, the next tenant-change guard cycle
// will skip the redirect and clear the flag. Used by cross-tenant
// resolution (e.g. shipment detail auto-resolving the correct tenant).
let _bypassNextGuard = false;

/**
 * Call before dispatching a programmatic tenant switch that should NOT
 * trigger the guard redirect (e.g. auto-resolving a shipment's tenant).
 * The flag is consumed (cleared) on the very next guard cycle.
 */
export function bypassTenantGuardOnce() {
  _bypassNextGuard = true;
}

/**
 * Watches for tenant switches and navigates away from tenant-scoped pages
 * (creation forms, drafts, detail pages) back to the module overview.
 * Resets relevant form slices when leaving creation/draft pages.
 */
export function useTenantChangeGuard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeTenantId = useAppSelector(selectActiveTenantId);
  const prevTenantRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (prevTenantRef.current === undefined) {
      prevTenantRef.current = activeTenantId;
      return;
    }

    if (activeTenantId && activeTenantId !== prevTenantRef.current) {
      if (_bypassNextGuard) {
        _bypassNextGuard = false;
        prevTenantRef.current = activeTenantId;
        return;
      }

      const guard = GUARDED_ROUTES.find((r) => r.match(pathname));
      if (guard) {
        guard.resetActions?.forEach((action) => dispatch(action()));
        navigate(guard.overview, { replace: true });
      }
    }

    prevTenantRef.current = activeTenantId;
  }, [activeTenantId, pathname, dispatch, navigate]);
}
